// Prefetches menu data for QR dining. Prevents duplicate calls via refs. PinAuth handles PIN independently.

import { useCallback, useContext, useEffect, useRef } from "react";
import { isEmpty } from "@swiggy-private/js-utils";
import {
    IApiConfigs,
    PinGenerationStatus,
    QrCodeActions,
    ResponseContext,
    SwiggyContextHeader,
    usePlApiContextFetch,
} from "@swiggy-private/portal-dineout-context";
import { useRouter } from "next/compat/router";

import { fetchMenuData, getRestaurantConfig } from "../api";
import { CURTAIN_RAISER_FREQUENCY_FLAG_TIMEOFFSET_MS } from "../components/CurtainRaiser/constants";
import { SUCCESS_STATUS_CODE, STORAGE_KEYS, FAILURE_STATUS_CODE } from "../constants";
import { isFrequencyCappedWithTableId } from "../utils/frequencyCapping";
import {
    menuTransformer,
    updateMenuItemInDish,
    updateMenuUI,
    ingestPinInMenuResponse,
    isMenuRoute,
} from "../utils/menu";
import { sendCustomNREvViaBlaze } from "../utils/newrelic";

interface IUseMenuDataPrefetchParams {
    restaurantId: string;
    tableId: string;
}

export const useMenuDataPrefetch = ({ restaurantId, tableId }: IUseMenuDataPrefetchParams) => {
    const router = useRouter();
    const { swiggyHeaders } = useContext(SwiggyContextHeader);
    const { dispatch, state } = useContext(ResponseContext);

    const makePlApiCall = usePlApiContextFetch();
    const lastRouteRef = useRef<string | null>(null);
    const hasFetchedRef = useRef({ menu: false });
    const hasFetchedCurtainRef = useRef(false);

    const currentPathname = router?.pathname ?? "";
    const isOnMenuPage = isMenuRoute(currentPathname);

    const prefetchMenu = useCallback(async () => {
        if (!makePlApiCall || !restaurantId || !tableId) return;

        dispatch({ type: QrCodeActions.MENU_SCREEN_LOADING, payload: {} });

        let finalLocalCart = {};
        try {
            const localCart = JSON.parse(sessionStorage.getItem(STORAGE_KEYS.LOCAL_CART) || "{}");
            finalLocalCart = localCart || {};
            dispatch({ type: QrCodeActions.UPDATE_LOCAL_CART, payload: finalLocalCart });
        } catch {
            sendCustomNREvViaBlaze("session_storage_table_config_error", {}, swiggyHeaders);
        }

        try {
            const menuResponse = await fetchMenuData({ tableId, restaurantId }, makePlApiCall);

            if (menuResponse?.statusCode === FAILURE_STATUS_CODE) {
                dispatch({ type: QrCodeActions.MENU_SCREEN_ERROR, payload: {} });
                return;
            }

            if (!isEmpty(menuResponse) && !isEmpty(menuResponse?.success)) {
                const { menuDataAfterDietOptions, selectedTab } = menuTransformer(menuResponse.success || []) as {
                    menuDataAfterDietOptions: unknown[];
                    selectedTab: string;
                };

                const transformedData = updateMenuItemInDish(menuDataAfterDietOptions || [], finalLocalCart);

                dispatch({ type: QrCodeActions.MENU_SCREEN_SUCCESS, payload: {} });
                dispatch({
                    type: QrCodeActions.MENU_SCREEN_UPDATE_REQUEST_ID,
                    payload: { requestId: menuResponse?.requestId || "" },
                });
                updateMenuUI(transformedData, selectedTab, dispatch);

                // Update configs in state
                const configs = menuResponse.success?.configs as IApiConfigs;
                if (configs) {
                    dispatch({
                        type: QrCodeActions.MENU_SCREEN_UPDATE_CONFIGS,
                        payload: { configs },
                    });
                }
                // Always inject pin if pin data exists (regardless of timing)
                const hasPinData = Boolean(state.pin?.data?.status);
                if (hasPinData && state.pin?.data) {
                    ingestPinInMenuResponse(
                        transformedData,
                        {
                            selectedTab,
                            pin: state.pin.data.pin,
                            tableNo: state.pin.data.tableNo,
                            status: state.pin.data.status as PinGenerationStatus,
                            userStatus: state.pin.data.userStatus,
                        },
                        dispatch,
                    );
                }
            }
        } catch {
            dispatch({ type: QrCodeActions.MENU_SCREEN_ERROR, payload: {} });
        }
    }, [makePlApiCall, restaurantId, tableId, dispatch, swiggyHeaders, state.pin?.data]);

    const prefetchCurtainRaiser = useCallback(async () => {
        if (!makePlApiCall || !restaurantId || !tableId) return;

        const isFrequencyCapped = await isFrequencyCappedWithTableId(
            STORAGE_KEYS.CURTAIN_RAISER,
            tableId,
            CURTAIN_RAISER_FREQUENCY_FLAG_TIMEOFFSET_MS,
        );
        if (isFrequencyCapped) return;

        dispatch({ type: QrCodeActions.CURTAIN_SCREEN_LOADING, payload: {} });
        try {
            const response = await getRestaurantConfig({ searchParams: { tableId, restaurantId } }, makePlApiCall);
            if (response?.statusCode === SUCCESS_STATUS_CODE) {
                dispatch({ type: QrCodeActions.CURTAIN_SCREEN_SUCCESS, payload: { data: response } });
            } else {
                dispatch({ type: QrCodeActions.CURTAIN_SCREEN_ERROR, payload: {} });
            }
        } catch {
            dispatch({ type: QrCodeActions.CURTAIN_SCREEN_ERROR, payload: {} });
        }
    }, [makePlApiCall, restaurantId, tableId, dispatch]);

    useEffect(() => {
        // Always update lastRouteRef first, before any early returns
        // This ensures we track ALL route changes for proper navigation detection
        const lastRoute = lastRouteRef.current;
        lastRouteRef.current = currentPathname;

        if (!isOnMenuPage || !restaurantId || !tableId || !makePlApiCall || !router?.isReady) {
            return;
        }

        // Determine if we're returning from a non-menu route and need to refetch
        // lastRoute === null means first visit, so no refetch needed
        const shouldRefetch = lastRoute !== null && !isMenuRoute(lastRoute);

        if (shouldRefetch) {
            // User returned from cart/orders - reset everything and refetch
            // Data might be stale (user may have placed an order)
            hasFetchedRef.current = { menu: false };
            dispatch({ type: QrCodeActions.MENU_SCREEN_RESET, payload: {} });
        }

        if (!hasFetchedRef.current.menu) {
            hasFetchedRef.current.menu = true;
            prefetchMenu();
        }

        if (!hasFetchedCurtainRef.current) {
            hasFetchedCurtainRef.current = true;
            prefetchCurtainRaiser();
        }
    }, [
        isOnMenuPage,
        restaurantId,
        tableId,
        makePlApiCall,
        router?.isReady,
        currentPathname,
        prefetchMenu,
        prefetchCurtainRaiser,
        dispatch,
    ]);

    return { isOnMenuPage };
};
